/**
 * Functions の「1 リクエストあたりの上限」の入力欄の範囲（2026-10-04 console/limits-ui・洗い出し L003）。
 * 画面は pages/FunctionsPage.tsx の LimitsCard、サーバーは GET …/functions の limitRanges（compute/serviceCore.ts の LIMITS）。
 *
 * WHY サーバーから読むか: 以前は画面に max={30000} を写していて、サーバーが 5,000ms に下げた後も「最大 30,000」を出し続けた
 *   （30,000 を入れると保存で断られる）。founder 方針（MASTER-PLAN §0・2026-10-04）で Compute が上限を Cloudflare の値まで上げていくので、
 *   画面は数字を持たず、サーバーの値に付いていく。
 * WHY 別ファイルか: node --test（型を剥がすだけ）で確かめられるように、JSX を持ち込まない。
 */

/** 1 つの上限の範囲（serviceCore.ts の LIMITS と同じ形） */
export interface LimitRange {
  min: number;
  max: number;
  default: number;
}

export type LimitRanges = Partial<Record<'cpuMs' | 'subRequests', LimitRange>>;

/** 1 回の呼び出しの 2 つの値（cpuMs / subRequests） */
export interface LimitPair {
  cpuMs: number;
  subRequests: number;
}

/**
 * GET …/functions の limits（compute/serviceLifecycle.ts の limitsView・2026-10-04 compute/limits-cpu）。
 * cpuMs / subRequests = **今実際に効く値**（残高で切った後）。requested = 本人が選んだ値。cpuMsCap = 今の残高で使える CPU の上限。
 * max = 選べる最大（Cloudflare と同じ）。requiredBalance = 選んだ CPU を全部使うのに要る前払い残高（セント・足りていれば null）。
 * 足したキーは古いサーバーには無い = すべて任意
 */
export interface FunctionsLimitsView extends LimitPair {
  requested?: LimitPair;
  cpuMsCap?: number;
  max?: LimitPair;
  requiredBalance?: number | null;
}

/**
 * <input type="number"> に付ける min / max。max は状態の limits.max（選べる最大）を優先し、無ければ limitRanges の max。
 * どちらも無い（古いサーバー）ときは何も付けない = 入力は止めず、保存したときのサーバーの判定（400）に任せる。古い数字を写して当てずっぽうに止めるより正しい。
 * WHY limits.max を優先するか（2026-10-05 console/followups）: Compute が上限を Cloudflare の値まで上げたとき、limits.max が
 *   「いま選べる最大」の正（limitsView）。limitRanges も同じ LIMITS から作られるが、片方だけ古いサーバーでも新しい方に合わせる
 */
export function limitInputRange(ranges: LimitRanges | undefined, key: keyof LimitRanges, max?: Partial<LimitPair>): { min?: number; max?: number } {
  const r = ranges?.[key];
  const top = max?.[key] ?? r?.max;
  return { ...(r ? { min: r.min } : {}), ...(top !== undefined ? { max: top } : {}) };
}

/**
 * 入力欄の最初の値 = 本人が選んだ値（requested）。
 * WHY（2026-10-05 console/followups）: 以前は効いている値（limits.cpuMs = 残高で切った後）から始めていた。残高が少なく 1,000ms に
 *   切られている人が「呼べる回数」だけを変えて保存すると、選んでいた 30,000ms が 1,000ms で上書きされ、入金しても戻らなかった
 *   （サーバーは選んだ値 × 残高の上限で効かせ、残高が増えると limitsFollow が追従する = 選んだ値を残すのが正しい）
 */
export function limitsFormStart(l: FunctionsLimitsView): LimitPair {
  return l.requested ?? { cpuMs: l.cpuMs, subRequests: l.subRequests };
}

/**
 * 選んだ CPU が今の残高の上限より大きい（= 一部しか効いていない）ときの説明の材料。サーバーが requiredBalance を数字で返したときだけ。
 * 足りている・古いサーバーは null = 何も出さない（画面で残高から計算し直さない = 式を写さない）
 */
export function balanceShortfall(l: FunctionsLimitsView): { capMs: number; wantMs: number; requiredBalance: number } | null {
  if (typeof l.requiredBalance !== 'number' || !l.requested) return null;
  return { capMs: l.cpuMsCap ?? l.cpuMs, wantMs: l.requested.cpuMs, requiredBalance: l.requiredBalance };
}
